/**
 * LRU cache of rendered PDF page bitmaps keyed by page + rotation + scale.
 * Lets page navigation feel instant when revisiting a page at the same
 * render bucket; full re-raster still happens on cache miss.
 */

export type PdfPageBitmapKey = {
  page: number;
  rotation: number;
  scale: number;
};

export type PdfPageBitmapEntry = {
  width: number;
  height: number;
  /** Pixel buffer from the offscreen canvas at paint time. */
  imageData: ImageData;
};

const DEFAULT_CAPACITY = 8;

function keyOf({ page, rotation, scale }: PdfPageBitmapKey): string {
  // Scale is bucketed already; round to avoid float-key churn.
  return `${page}|${rotation}|${Math.round(scale * 1000) / 1000}`;
}

export class PdfPageBitmapCache {
  private readonly capacity: number;
  private readonly map = new Map<string, PdfPageBitmapEntry>();

  constructor(capacity = DEFAULT_CAPACITY) {
    this.capacity = Math.max(1, capacity);
  }

  get(key: PdfPageBitmapKey): PdfPageBitmapEntry | undefined {
    const k = keyOf(key);
    const hit = this.map.get(k);
    if (!hit) return undefined;
    // Refresh LRU order.
    this.map.delete(k);
    this.map.set(k, hit);
    return hit;
  }

  set(key: PdfPageBitmapKey, entry: PdfPageBitmapEntry): void {
    const k = keyOf(key);
    if (this.map.has(k)) this.map.delete(k);
    this.map.set(k, entry);
    while (this.map.size > this.capacity) {
      const oldest = this.map.keys().next().value;
      if (oldest == null) break;
      this.map.delete(oldest);
    }
  }

  clear(): void {
    this.map.clear();
  }

  get size(): number {
    return this.map.size;
  }
}

/** Blit a cached entry onto a visible canvas (resizes the target). */
export function blitPdfPageBitmap(
  canvas: HTMLCanvasElement,
  entry: PdfPageBitmapEntry,
): void {
  canvas.width = entry.width;
  canvas.height = entry.height;
  const ctx = canvas.getContext("2d");
  ctx?.putImageData(entry.imageData, 0, 0);
}

/** Snapshot the current offscreen canvas into a cache entry. */
export function snapshotPdfPageBitmap(
  source: HTMLCanvasElement,
): PdfPageBitmapEntry | null {
  // Prefer CPU-backed readback when this canvas is used for repeated
  // getImageData (Chrome Canvas2D warning otherwise). No-op if a context
  // was already created without the attribute.
  const ctx = source.getContext("2d", { willReadFrequently: true });
  if (!ctx || source.width <= 0 || source.height <= 0) return null;
  try {
    return {
      width: source.width,
      height: source.height,
      imageData: ctx.getImageData(0, 0, source.width, source.height),
    };
  } catch {
    // Tainted canvas / security — skip caching rather than throw.
    return null;
  }
}
